// AUTHOR: Rahul Choudhary | Whirldata Inc.
// DATE: 23 Aug 2022
// DESCRIPTION:
// -- Header of the whole web-app.
// -- Contains tabs and icons to navigate amongst pages.
// -- logout method (called on icon click) replaces /login page with this page
//    thus removing it from navigation stack and clears the <cookies/localstorage>.

// -- Components:
//     -- TabNavItem: defines the text based navigation tabs and decides
//         which page to show based on its active and inactive state.
//         A tab turns active on click and relate page shows below the header.
//     -- TabContent: defines the content/page to show for each tab
//         based on its active/inactive state.
//     -- TabIcons: defines the navigation icons and mimics TabNavItem
//         in terms of behaviour. Difference being this component takes in icons as view
//         as opposed to text based items.

import { BellOutlined, CompassOutlined, DashboardOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../assets/logo_long.png';
import ErrorAlert from '../components/ErrorAlert';
import Search from '../components/Search';
import TabContent from '../components/TabContent';
import TabNavItem from '../components/TabNavItem';
import TechnologyDrawer from '../components/TechnologyDrawer';
import { AssetObj } from '../interfaces/AssetProps';
import { getRole, getTechnology, getTechnologyDisplayValue, getUser } from '../services/helper';
import './Home.css';
import NetworkActivity from './NetworkActivity';
import Outdoor from './Outdoor';
import Profile from './Profile';
import Settings from './Settings';
//@ts-ignore

const Home: React.FC = () => {
  const navigate = useNavigate(); // Hook for navigation used in loginClicked()
  // const client = axios.create({
  //   baseURL: `${window.location.protocol}//${window.location.hostname}${window.location.hostname === 'localhost' ? ':5000' : '/api'}`,
  // });

  // State Hooks
  const [activeTab, setActiveTab] = useState('outdoor');
  const [errorAlert, setErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isInstaller, setIsInstaller] = useState(false);
  const [isCPI, setIsCPI] = useState(false);
  const [isEngineer, setIsEngineer] = useState(false);
  const [assetToFocus, setAssetToFocus] = useState<AssetObj | undefined>(undefined); // Asset to focus on map if redirected from NetworkActivity page.
  const [searchFocus, setSearchFocus] = useState(false);
  const [center, setCenter] = useState({
    lat: 39.600292,
    lng: -104.889944,
  });
  const [currentPage, setCurrentPage] = useState('home');
  const [searchedLocation, setSearchedLocation] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [username, setUsername] = useState('');
  const [activeTechnology, setActiveTechnology] = useState(getTechnology());
  const [boundsPolygon, setBoundsPolygon] = useState<any>(null);

  useEffect(() => {
    setIsInstaller(getRole() === 'installer');
    setIsCPI(getRole() === 'cpi');
    setIsEngineer(getRole() === 'engineer');
  }, []);

  useEffect(() => {
    if (assetToFocus) {
      setActiveTab('outdoor');
    }
  }, [assetToFocus]);

  useEffect(() => {
    setUsername(getUser());
  }, []);

  useEffect(() => {
    if (activeTab !== 'outdoor') {
      setAssetToFocus(undefined); // Clear assetToFocus if user navigates away from outdoor page.
    }
  }, [activeTab]);

  // Methods
  const logout = () => {
    localStorage.clear();
    navigate('/login', { replace: true });
    // let accessToken = localStorage.getItem("access_token");
    // let headers: any = {
    //   "Content-Type": "application/json",
    //   Accept: "application/json",
    //   Authorization: `Bearer ${accessToken}`,
    // };
    // clien
    //   .post(apiConfig["api"]["logout"]["url"], {}, { headers: headers })
    //   .then((response: any) => {
    //     localStorage.clear();
    //     navigate("/login", { replace: true });
    //   })
    //   .catch((error: any) => {
    //     if (error.response.status === 401) {
    //       localStorage.clear();
    //       navigate("/login", { replace: true });
    //     } else {
    //       setErrorMessage(error.response.data.message);
    //       setErrorAlert(true);
    //     }
    //   });
  };

  useEffect(() => {
    if (openDrawer) {
      setCurrentPage('home');
      setSearchFocus(false);
    }
  }, [openDrawer]);

  useEffect(() => {
    setCurrentPage('home');
  }, [activeTab]);
  
  useEffect(() => {
    if (searchFocus) {
      setCurrentPage('home');
      setOpenDrawer(false);
    }
  }, [searchFocus])

  // View
  return (
    <div>
      <div className="header" id="header">
        {/* Logo of C3 Spectra : takes to outdoor page on clicked */}
        <div className="vertical-center">
          <img
            className="header-logo"
            src={Logo}
            onClick={() => {
              setActiveTab('outdoor');
            }}
            alt="c3-spectra logo"></img>
        </div>

        {/* Navigation Tabs */}
        <ul className="nav">
          <TabNavItem icon={<CompassOutlined />} title="Map" id="outdoor" activeTab={activeTab} setActiveTab={setActiveTab} />
          <TabNavItem
            icon={<DashboardOutlined />}
            show={!isInstaller}
            title="Network Activity"
            id="netactivity"
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          {/* <TabNavItem show={!isInstaller} title="Data/Export" id="data-export" activeTab={activeTab} setActiveTab={setActiveTab} /> */}
        </ul>

        {/* <div className="right row-wise vertical-center gap-8 right"> */}
        <Search
          setPlace={(coordinates: any, gotToCenter: boolean = true) => {
            if (activeTab == 'netactivity') {
              setActiveTab('outdoor');
              localStorage.setItem('center', JSON.stringify(coordinates));
            }
            if (gotToCenter) setCenter(coordinates);
            setSearchedLocation(true);
          }}
          closeSearch={() => {
            setCurrentPage('home');
          }}
          searchFocus={searchFocus}
          setSearchFocus={setSearchFocus}
          setBoundsPolygon={setBoundsPolygon}
        />
        {/* </div> */}
        {/* Navigation Icons */}
        <div className="row-wise vertical-center gap-8">
          {/* <TabIcons show={!isInstaller && !isCPI && !isEngineer} icon="settings" id="settings" activeTab={activeTab} setActiveTab={setActiveTab} />
          <TabIcons icon="person" id="profile" activeTab={activeTab} setActiveTab={setActiveTab} /> */}

          <div className="row-wise vertical-center height-search">
            <div onClick={() => setOpenDrawer(true)} className="row-wise vertical-center background-secondary border-left-only h-btn cursor-pointer">
              <span className="tech-text f-14 l-24">Technology</span>
              <span className="technology-item l-24">{getTechnologyDisplayValue[getTechnology()]}</span>
            </div>
            <div onClick={() => setActiveTab('settings')} className="row-wise vertical-center height-search border-right cursor-pointer">
              {/* <span className='material-icons-outlined icon-size-header'>
                settings
              </span> */}
              <SettingOutlined className="text-color" />
            </div>
          </div>
          {/* Logout */}
          {/* <div className="icon-background" onClick={logout}>
            <span className="material-icons-round icon font-20">logout</span>
          </div> */}
          <div className="divider-vertical"></div>
          <BellOutlined className="text-color icon-bell d-none" />
          <div onClick={() => setActiveTab('profile')} className="border">
            {localStorage.getItem('profilePic') ? (
              <img className="profile-pic-avatar" src={localStorage.getItem('profilePic') as string} />
            ) : username ? (
              username.charAt(0).toUpperCase()
            ) : (
              <UserOutlined />
            )}
          </div>
        </div>
      </div>
      {/* Content of the Navigation Tabs and Navigation Icons */}
      <div>
        <TabContent id="outdoor" activeTab={activeTab}>
          <Outdoor
            preSelectedAsset={assetToFocus}
            goToProfile={() => {
              setActiveTab('settings'); // changed to setting page because of change in UI/UX design
            }}
            searchedLocationMap={searchedLocation}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            centerMap={center}
            activeTechnology={activeTechnology}
            boundsPolygon={boundsPolygon}
          />
        </TabContent>
        <TabContent id="netactivity" activeTab={activeTab}>
          <NetworkActivity
            takeToMap={(asset: AssetObj) => {
              setAssetToFocus(asset);
            }}
          />
        </TabContent>
        {/* <TabContent id="data-export" activeTab={activeTab}>
          <DataExport />
        </TabContent> */}
        <TabContent id="settings" activeTab={activeTab}>
          <Settings />
        </TabContent>
        <TabContent id="profile" activeTab={activeTab}>
          <Profile username={username} setUsername={setUsername} />
        </TabContent>
      </div>

      <ErrorAlert
        show={errorAlert}
        onDismiss={() => {
          setErrorAlert(false);
        }}
        message={errorMessage}
      />
      {openDrawer && <TechnologyDrawer open={openDrawer} setOpenDrawer={setOpenDrawer} setActiveTechnology={setActiveTechnology} />}
    </div>
  );
};

export default Home;
